import type { ProjectStandardSnapshot, StandardSurface } from "../domain/renovationStandard";

/**
 * WHAT THIS HOUSE TAUGHT ME ABOUT HOW I RENOVATE (spec §91-11/12/13/14).
 *
 * A project starts from a frozen snapshot of a Renovation Standard and then
 * gets changed, because a real renovation always does. This engine compares
 * the snapshot with the project's FINAL REUSABLE CONFIGURATION and says
 * which differences are company knowledge worth keeping. It is pure: it
 * writes nothing, and a standard changes only when somebody ticks a
 * difference and saves.
 *
 * THE LINE THIS FILE DEFENDS:
 *
 *   The STANDARD answers "what and how do we normally renovate?"
 *   The ESTIMATING PROFILE answers "what do we expect it to cost?"
 *
 * They are never collapsed. An actual price, a quote, a measured quantity
 * and anything else that belongs to THIS property can never become company
 * knowledge, however convenient it would be — a standard carrying Project
 * A's 214 sq ft of bedroom floor would silently mis-scope Project B, and a
 * standard carrying Project A's final flooring invoice would quietly become
 * a price book competing with the profile.
 *
 * A ROOM EXCEPTION IS NOT A NEW DEFAULT (spec §91-35). Carpet in one
 * bedroom is a fact about that bedroom. It is reported so the user can see
 * it, and it is NOT offered as the company's whole-house flooring, because
 * the standard has no per-room finish to hold it in.
 */

export type StandardChangeKind = "finish" | "spec" | "room-package";

/** The reusable slot a difference would write to, if saved. */
export type StandardWrite =
  | { kind: "finish"; surface: StandardSurface; materialKey: string }
  | { kind: "spec"; featureDefinitionId: string; assemblyTemplateId: string | null }
  | { kind: "room-package"; roomTypeId: string; packageId: string };

export interface StandardChange {
  /** Stable selection key — what the preview's tick box is keyed by. */
  id: string;
  kind: StandardChangeKind;
  /** "Flooring — whole home", "Bathroom vanity specification", "Bedroom rooms". */
  label: string;
  /** What the standard said when this project started (null = it said nothing). */
  standardValue: string | null;
  /** What this project ended up using. */
  projectValue: string | null;
  write: StandardWrite | null;
  /** A company-wide reusable choice, or an exception belonging to one room. */
  scope: "project-wide" | "room-exception";
  /** May the user save this one? A room exception and an ambiguity may not. */
  offered: boolean;
  /** Always present: why it is offered, or why it cannot be. */
  note: string;
}

export interface StandardComparison {
  standardId: string | null;
  standardName: string | null;
  /** True when the standard this project started from still exists. */
  standardExists: boolean;
  changes: StandardChange[];
  /** Differences that may be saved — what the preview ticks by default is none of them. */
  offeredCount: number;
}

export interface StandardComparisonInput {
  snapshot: ProjectStandardSnapshot | null;
  standardExists: boolean;
  /** The project's own §82 project-level finish defaults as they stand now. */
  projectFinishes: ReadonlyArray<{ surface: StandardSurface; materialKey: string }>;
  /** §82 room-level overrides — reported, never promoted. */
  roomOverrides: ReadonlyArray<{ surface: StandardSurface; materialKey: string; roomLabel: string }>;
  /**
   * The specification this project actually used for a kind of work: the
   * assembly attached to its live work items. distinctCount > 1 means the
   * project used SEVERAL, which is a fact about this property rather than a
   * company choice, so nothing is guessed.
   */
  projectSpecs: ReadonlyArray<{
    featureDefinitionId: string;
    definitionName: string;
    assemblyTemplateId: string;
    distinctCount: number;
  }>;
  /** The package each kind of room actually received. */
  projectRoomPackages: ReadonlyArray<{
    roomTypeId: string;
    roomTypeName: string;
    packageId: string;
    distinctCount: number;
  }>;
  materialLabels: ReadonlyMap<string, string>;
  assemblyNames: ReadonlyMap<string, string>;
  packageNames: ReadonlyMap<string, string>;
  /**
   * The assembly Berrier Reno would have attached by itself: the ONE
   * unit-compatible suggestion for a kind of work (Phase 8.1), where there
   * is exactly one. Somebody accepting a default made no decision, so
   * offering to write it into the company's standard would dress the
   * software's own seed up as company knowledge — found by running the
   * §91 walkthrough, which offered ten of them.
   */
  impliedSpecs?: ReadonlyMap<string, string>;
}

/**
 * EVERYTHING THAT MAY NEVER BE SAVED BACK (spec §91-14), written down so a
 * test can hold the line rather than a reviewer having to remember it.
 * These are property facts and money; a standard holds neither.
 */
export const NEVER_SAVED_BACK: readonly string[] = [
  "room dimensions",
  "floor area",
  "perimeter",
  "wall area",
  "roof area",
  "cabinet quantity",
  "countertop quantity",
  "fixture counts",
  "project address",
  "acquisition price",
  "ARV",
  "rent",
  "financing",
  "contractor quotes",
  "actual expenses",
  "purchases",
  "receipts",
  "schedule dates",
  "completion state",
  "design geometry",
  "annotations",
  "project notes",
  "historical actual costs",
  "calibrated rates",
];

const SURFACE_LABEL: Record<StandardSurface, string> = {
  floor: "Flooring — whole home",
  walls: "Wall paint — whole home",
  ceiling: "Ceiling paint — whole home",
};

export function compareStandard(input: StandardComparisonInput): StandardComparison {
  const snapshot = input.snapshot;
  const changes: StandardChange[] = [];
  const material = (key: string) => input.materialLabels.get(key) ?? key;

  /* ---- whole-house finishes (the standard's own slots) ---- */
  const snapshotFinish = new Map<StandardSurface, string>((snapshot?.finishes ?? []).map((f) => [f.surface, f.materialKey]));
  const projectFinish = new Map<StandardSurface, string>(input.projectFinishes.map((f) => [f.surface, f.materialKey]));
  for (const surface of ["floor", "walls", "ceiling"] as StandardSurface[]) {
    const was = snapshotFinish.get(surface) ?? null;
    const now = projectFinish.get(surface) ?? null;
    if (now === null || was === now) continue;
    changes.push({
      id: `finish:${surface}`,
      kind: "finish",
      label: SURFACE_LABEL[surface],
      standardValue: was === null ? null : material(was),
      projectValue: material(now),
      write: { kind: "finish", surface, materialKey: now },
      scope: "project-wide",
      offered: true,
      note:
        was === null
          ? "The standard did not choose this surface. Saving it means every future project starts with it."
          : "This is a whole-house choice, so saving it changes what future projects start with.",
    });
  }

  /* ---- room exceptions: reported, never promoted (spec §91-35) ---- */
  for (const override of input.roomOverrides) {
    const wholeHouse = projectFinish.get(override.surface) ?? null;
    if (wholeHouse !== null && wholeHouse === override.materialKey) continue;
    changes.push({
      id: `room-exception:${override.roomLabel}:${override.surface}:${override.materialKey}`,
      kind: "finish",
      label: `${material(override.materialKey)} in ${override.roomLabel}`,
      standardValue: wholeHouse === null ? null : material(wholeHouse),
      projectValue: material(override.materialKey),
      write: null,
      scope: "room-exception",
      offered: false,
      note: "This is an exception for one room, not how the whole house was finished — a standard has no per-room finish to keep it in.",
    });
  }

  /* ---- specifications: the assembly that prices a kind of work ---- */
  const snapshotSpec = new Map((snapshot?.specs ?? []).map((s) => [s.featureDefinitionId, s.assemblyTemplateId]));
  for (const spec of input.projectSpecs) {
    const was = snapshotSpec.get(spec.featureDefinitionId) ?? null;
    if (was === spec.assemblyTemplateId) continue;
    const assembly = (id: string | null) => (id === null ? null : (input.assemblyNames.get(id) ?? id));
    if (spec.distinctCount > 1) {
      changes.push({
        id: `spec:${spec.featureDefinitionId}`,
        kind: "spec",
        label: `${spec.definitionName} specification`,
        standardValue: assembly(was),
        projectValue: `${spec.distinctCount} different specifications on this project`,
        write: null,
        scope: "project-wide",
        offered: false,
        note: "This project used more than one specification for this work, so which one is the company's normal choice is not something to guess.",
      });
      continue;
    }
    const implied = input.impliedSpecs?.get(spec.featureDefinitionId) ?? null;
    if (was === null && implied !== null && implied === spec.assemblyTemplateId) {
      changes.push({
        id: `spec:${spec.featureDefinitionId}`,
        kind: "spec",
        label: `${spec.definitionName} specification`,
        standardValue: null,
        projectValue: assembly(spec.assemblyTemplateId),
        write: null,
        scope: "project-wide",
        offered: false,
        note: "This is the only specification Berrier Reno has for this work, so it was attached by default — there is no decision here to record.",
      });
      continue;
    }
    changes.push({
      id: `spec:${spec.featureDefinitionId}`,
      kind: "spec",
      label: `${spec.definitionName} specification`,
      standardValue: assembly(was),
      projectValue: assembly(spec.assemblyTemplateId),
      write: { kind: "spec", featureDefinitionId: spec.featureDefinitionId, assemblyTemplateId: spec.assemblyTemplateId },
      scope: "project-wide",
      offered: true,
      note:
        was === null
          ? "The standard had no specification for this work, and this project chose one. Saving it means future projects start with it."
          : "Saving this changes which specification future projects start with. It changes no price.",
    });
  }

  /* ---- room packages ---- */
  const snapshotPackage = new Map((snapshot?.roomPackages ?? []).map((r) => [r.roomTypeId, r.packageId]));
  for (const applied of input.projectRoomPackages) {
    const was = snapshotPackage.get(applied.roomTypeId) ?? null;
    if (was === applied.packageId) continue;
    const packageName = (id: string | null) => (id === null ? null : (input.packageNames.get(id) ?? id));
    if (applied.distinctCount > 1) {
      changes.push({
        id: `room-package:${applied.roomTypeId}`,
        kind: "room-package",
        label: `${applied.roomTypeName} rooms`,
        standardValue: packageName(was),
        projectValue: `${applied.distinctCount} different packages on this project`,
        write: null,
        scope: "project-wide",
        offered: false,
        note: "Different rooms of this kind got different packages here, so there is no single company choice to save.",
      });
      continue;
    }
    changes.push({
      id: `room-package:${applied.roomTypeId}`,
      kind: "room-package",
      label: `${applied.roomTypeName} rooms`,
      standardValue: packageName(was),
      projectValue: packageName(applied.packageId),
      write: { kind: "room-package", roomTypeId: applied.roomTypeId, packageId: applied.packageId },
      scope: "project-wide",
      offered: true,
      note:
        was === null
          ? "The standard had no package for this kind of room. Saving it means future projects start with this one."
          : "Saving this changes which package future projects start with for this kind of room.",
    });
  }

  changes.sort((a, b) => Number(b.offered) - Number(a.offered) || a.label.localeCompare(b.label));
  return {
    standardId: snapshot?.standardId ?? null,
    standardName: snapshot?.name ?? null,
    standardExists: input.standardExists,
    changes,
    offeredCount: changes.filter((c) => c.offered).length,
  };
}
